import * as React from 'react';
import { MainLoggedInLayout } from "../../components/layouts/main.loggedIn.layout"
import { Avatar, Box, Card, CircularProgress, IconButton, TextField, Typography } from '@mui/material';
import { Close, Search, Send } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { UserContext } from '../../context/UserContext';
import { getRelationship, searchAccounts } from '../../controllers/accounts/accounts';
import { createComment } from '../../controllers/posts/comments';
import { createConversation } from '../../controllers/conversation/conversation';

const NAV = '#202c41';
const BG = '#F2F4F8';
const MUTED = '#8A95A3';

export const MessageCreate = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { userDetails } = React.useContext(UserContext);
    const token = userDetails?.token ?? '';

    const [query, setQuery] = React.useState('');
    const [results, setResults] = React.useState([]);
    const [searching, setSearching] = React.useState(false);
    const [selected, setSelected] = React.useState(location.state?.prefillAccount ?? null);
    const [content, setContent] = React.useState('');
    const debounceRef = React.useRef(null);

    React.useEffect(() => {
        if (!query || query.length < 2) { setResults([]); return }
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setSearching(true);
            try {
                const accounts = await searchAccounts(query, token);
                setResults(Array.isArray(accounts) ? accounts.filter(a => a.id !== userDetails?.id) : []);
            } finally {
                setSearching(false);
            }
        }, 300);
        return () => clearTimeout(debounceRef.current);
    }, [query, token, userDetails?.id]);

    const { data: relationship, isLoading: loadingRelationship } = useQuery({
        queryKey: ['getRelationship', selected?.id],
        queryFn: () => getRelationship(selected.id, token),
        enabled: !!selected?.id,
    });

    const canDirectMessage = !!relationship?.followed_by;

    const mutation = useMutation({
        mutationFn: async ({ body, token }) => {
            const status = await createComment({ body, token });
            // fetch() doesn't throw on 4xx/5xx — Mastodon returns a plain
            // {error: "..."} body on rejection, so status.id being missing is
            // the only signal something went wrong.
            if (!status?.id) {
                throw new Error('Mastodon rejected the message: ' + JSON.stringify(status));
            }
            // Register the new thread with the sidecar so it shows up via
            // GET /conversation/mine instead of relying on polling Mastodon.
            const conversation = await createConversation({
                token,
                participants: [userDetails?.id, selected?.id],
                status_id: status.id,
                author_id: userDetails?.id,
            });
            if (!conversation?.conversation?._id) {
                throw new Error('Server rejected the conversation: ' + JSON.stringify(conversation));
            }
            return status;
        },
        onSuccess: () => navigate('/messages'),
        onError: (err) => console.error('MessageCreate: send failed', err),
    });

    const send = () => {
        if (!content.trim() || !selected) return;
        mutation.mutate({
            body: {
                status: '@' + selected.acct + ' ' + content,
                visibility: canDirectMessage ? 'direct' : 'public',
            },
            token,
        });
    };

    return (
        <MainLoggedInLayout>
            <Box sx={{ maxWidth: 560 }}>
                <Typography variant="h5" fontWeight={800} sx={{
                    mb: 3,
                    background: 'linear-gradient(90deg,#E40303,#732982)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                }}>
                    New Message
                </Typography>

                {!selected ? (
                    <>
                        <TextField
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search for someone to message…"
                            variant="outlined"
                            size="small"
                            fullWidth
                            InputProps={{
                                startAdornment: <Search sx={{ color: '#732982', mr: 1 }} />,
                                endAdornment: searching && <CircularProgress size={18} />,
                            }}
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 50,
                                    background: '#fff',
                                    '& fieldset': { borderColor: 'rgba(115,41,130,0.25)' },
                                    '&:hover fieldset': { borderColor: '#732982' },
                                    '&.Mui-focused fieldset': { borderColor: '#732982' },
                                },
                            }}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {results.map((account) => (
                                <Card
                                    key={account.id}
                                    onClick={() => setSelected(account)}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
                                        borderRadius: 3, cursor: 'pointer',
                                        transition: 'transform 0.15s, box-shadow 0.15s',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                                        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(0,0,0,0.1)' },
                                    }}
                                >
                                    <Avatar src={account.avatar} sx={{ width: 44, height: 44 }} />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ fontWeight: 700, fontSize: 14, color: NAV }} noWrap>
                                            {account.display_name || account.username}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: MUTED }} noWrap>@{account.acct}</Typography>
                                    </Box>
                                </Card>
                            ))}
                            {query.length >= 2 && !searching && results.length === 0 && (
                                <Typography variant="body2" sx={{ color: MUTED, textAlign: 'center', mt: 3 }}>
                                    No one found matching "{query}"
                                </Typography>
                            )}
                        </Box>
                    </>
                ) : (
                    <Card sx={{ p: 3, borderRadius: 3, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <Avatar src={selected.avatar} sx={{ width: 44, height: 44 }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: 14, color: NAV }} noWrap>
                                    {selected.display_name || selected.username}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: MUTED }} noWrap>@{selected.acct}</Typography>
                            </Box>
                            <IconButton onClick={() => { setSelected(null); setContent('') }} size="small">
                                <Close />
                            </IconButton>
                        </Box>

                        {loadingRelationship ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={22} />
                            </Box>
                        ) : (
                            <>
                                <Typography variant="body2" sx={{ color: MUTED, mb: 2 }}>
                                    {canDirectMessage
                                        ? `@${selected.acct} follows you, so this will be sent as a private message.`
                                        : `@${selected.acct} doesn't follow you yet, so this will be sent as a public message that mentions them. Once they follow you back, you can send private messages.`}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                                    <TextField
                                        value={content}
                                        onChange={(e) => setContent(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }}
                                        placeholder={canDirectMessage ? 'Send a private message…' : 'Send a public message…'}
                                        multiline
                                        maxRows={4}
                                        fullWidth
                                        size="small"
                                        sx={{
                                            background: BG,
                                            '& .MuiOutlinedInput-root': { borderRadius: 4, '& fieldset': { border: 'none' } },
                                        }}
                                    />
                                    <IconButton
                                        onClick={send}
                                        disabled={!content.trim() || mutation.isPending}
                                        sx={{
                                            background: content.trim() ? NAV : BG,
                                            border: content.trim() ? 'none' : '1px solid #DDE4EF',
                                            color: content.trim() ? '#fff' : MUTED,
                                            width: 40, height: 40,
                                            '&:hover': { background: content.trim() ? '#16202f' : BG },
                                            '&.Mui-disabled': { color: MUTED },
                                        }}
                                    >
                                        {mutation.isPending ? <CircularProgress size={18} sx={{ color: content.trim() ? '#fff' : MUTED }} /> : <Send sx={{ fontSize: 18 }} />}
                                    </IconButton>
                                </Box>
                                {mutation.isError && (
                                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                        Couldn't send that. {mutation.error?.message}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Card>
                )}
            </Box>
        </MainLoggedInLayout>
    )
}
